/*
 * Page geometry for the gallery PDF/PPTX export, in inches.
 *
 * Pages are laid out from the paper size, never from the on-screen preview: the old export
 * screenshotted the preview and stretched it onto the page, so the output depended on the
 * user's window shape (images came out 25-40% too tall) and on the browser's ability to paint
 * an SVG foreignObject (Safari/Firefox painted nothing, which the JPEG turned black).
 */

export interface IBox {
    x: number;
    y: number;
    w: number;
    h: number;
}

export interface ITextBlock {
    box: IBox;
    lines: Array<string>;
    fontPt: number;
    paddingIn: number;
    lineHeightIn: number;
}

export interface IPageGrid {
    header?: IBox;
    subHeader?: IBox;
    tiles: Array<IBox>;
}

// The PDF and PPTX renderers share one measurer so text wraps identically in both, and the
// overlay box is sized to the lines that will actually be drawn.
export interface ITextMeasurer {
    widthOf(text: string, fontPt: number): number;
    splitToWidth(text: string, fontPt: number, maxWidthIn: number): Array<string>;
}

export const GUTTER_IN: number = 0.02;
export const HEADER_HEIGHT_RATIO: number = 0.05;
export const HEADER_FONT_RATIO: number = 0.4;
export const SUB_HEADER_FONT_RATIO: number = 0.25;
export const HEADER_PADDING_IN: number = 0.08;
export const LINE_HEIGHT_FACTOR: number = 1.2;

const POINTS_PER_INCH: number = 72;
const MIN_METRIC_FONT_PT: number = 6.5;
const MAX_METRIC_FONT_PT: number = 10;
const METRIC_FONT_PT_PER_TILE_INCH: number = 3.6;

export function parseLayout(layout: string): { cols: number; rows: number } {
    const [cols, rows]: Array<number> = layout.split('x').map(Number);

    return { cols, rows };
}

export function computePageGrid(
    pageWidth: number,
    pageHeight: number,
    cols: number,
    rows: number,
    hasHeader: boolean,
    hasSubHeader: boolean
): IPageGrid {
    const barHeight: number = pageHeight * HEADER_HEIGHT_RATIO;
    const grid: IPageGrid = { tiles: [] };
    let top: number = 0;

    if (hasHeader) {
        grid.header = { x: 0, y: top, w: pageWidth, h: barHeight };
        top += barHeight;
    }
    if (hasSubHeader) {
        grid.subHeader = { x: 0, y: top, w: pageWidth, h: barHeight };
        top += barHeight;
    }
    if (top > 0) {
        top += GUTTER_IN;
    }

    const tileWidth: number = (pageWidth - (cols - 1) * GUTTER_IN) / cols;
    const tileHeight: number = (pageHeight - top - (rows - 1) * GUTTER_IN) / rows;

    for (let row: number = 0; row < rows; row++) {
        for (let col: number = 0; col < cols; col++) {
            grid.tiles.push({
                x: col * (tileWidth + GUTTER_IN),
                y: top + row * (tileHeight + GUTTER_IN),
                w: tileWidth,
                h: tileHeight
            });
        }
    }

    return grid;
}

export function fitContain(box: IBox, naturalWidth: number, naturalHeight: number): IBox {
    const scale: number = Math.min(box.w / naturalWidth, box.h / naturalHeight);
    const w: number = naturalWidth * scale;
    const h: number = naturalHeight * scale;

    return { x: box.x + (box.w - w) / 2, y: box.y + (box.h - h) / 2, w, h };
}

export function metricFontPt(tileWidth: number): number {
    return Math.min(MAX_METRIC_FONT_PT, Math.max(MIN_METRIC_FONT_PT, tileWidth * METRIC_FONT_PT_PER_TILE_INCH));
}

export function composeOverlay(
    tile: IBox,
    rows: Array<string>,
    alignTop: boolean,
    measurer: ITextMeasurer
): ITextBlock {
    const fontPt: number = metricFontPt(tile.w);
    const paddingIn: number = (fontPt * 0.6) / POINTS_PER_INCH;
    const lineHeightIn: number = (fontPt * LINE_HEIGHT_FACTOR) / POINTS_PER_INCH;
    const wrapped: Array<string> = rows.reduce(
        (lines: Array<string>, row: string) =>
            lines.concat(measurer.splitToWidth(row.toUpperCase(), fontPt, tile.w - 2 * paddingIn)),
        []
    );
    // The preview clips the overlay at the tile edge; without the same cut, many metrics on a
    // 6x3 page would run into the tile below.
    const maxLines: number = Math.max(0, Math.floor((tile.h - 2 * paddingIn) / lineHeightIn));
    const lines: Array<string> = wrapped.slice(0, maxLines);
    const h: number = lines.length * lineHeightIn + 2 * paddingIn;

    return {
        box: { x: tile.x, y: alignTop ? tile.y : tile.y + tile.h - h, w: tile.w, h },
        lines,
        fontPt,
        paddingIn,
        lineHeightIn
    };
}

export function composeHeaderBar(
    bar: IBox,
    text: string,
    fontRatio: number,
    measurer: ITextMeasurer
): ITextBlock {
    const baseFontPt: number = bar.h * POINTS_PER_INCH * fontRatio;
    const available: number = bar.w - 2 * HEADER_PADDING_IN;
    const width: number = measurer.widthOf(text, baseFontPt);
    // Shrink rather than wrap: the bar is one line tall, and a wrapped header would spill
    // over the first row of products.
    const fontPt: number = width > available ? (baseFontPt * available) / width : baseFontPt;

    return {
        box: bar,
        lines: [text],
        fontPt,
        paddingIn: HEADER_PADDING_IN,
        lineHeightIn: (fontPt * LINE_HEIGHT_FACTOR) / POINTS_PER_INCH
    };
}

/*
 * Points an image URL at the full-size copy Picture Manager keeps beside the /mini/ and
 * /small/ ones. Only the folder directly above the file name is swapped: a substring replace
 * (as the dialog opener's 'web' -> 'mini' does) would also rewrite "web" inside a file name.
 */
export function toOriginalImageUrl(url: string): string {
    if (!url || url.includes('pattern-no-image')) {
        return url;
    }

    return url.replace(/\/(small|mini|web)\/([^/?#]+)(?=[?#]|$)/, '/original/$2');
}

export async function mapWithConcurrency<T, R>(
    items: Array<T>,
    limit: number,
    task: (item: T, index: number) => Promise<R>
): Promise<Array<R>> {
    const results: Array<R> = new Array(items.length);
    let next: number = 0;

    const worker: () => Promise<void> = async (): Promise<void> => {
        while (next < items.length) {
            const index: number = next++;
            results[index] = await task(items[index], index);
        }
    };

    await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));

    return results;
}
